import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, X, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = () => ({
  name: "", sku: "", description: "",
  is_active: true,
  ingredients: [],
});

export default function Blends() {
  const { user } = useAuth();
  const canWrite = ["Owner", "ProductionManager"].includes(user?.role);

  const [blends, setBlends] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    const [b, o] = await Promise.all([api.get("/blends"), api.get("/origins")]);
    setBlends(b.data);
    setOrigins(o.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setError(""); setOpen(true); };
  const openEdit = (b) => {
    setForm({ ...b, ingredients: b.ingredients || [] });
    setEditId(b.id);
    setError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      name: form.name, sku: form.sku, description: form.description,
      is_active: !!form.is_active,
      ingredients: form.ingredients
        .filter((i) => i.origin_id)
        .map((i) => ({ origin_id: i.origin_id, percentage: parseFloat(i.percentage) || 0 })),
    };
    try {
      if (editId) await api.patch(`/blends/${editId}`, payload);
      else await api.post("/blends", payload);
      setOpen(false);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this blend?")) return;
    await api.delete(`/blends/${id}`);
    await load();
  };

  const addIng = () => setForm({ ...form, ingredients: [...form.ingredients, { origin_id: "", percentage: 0 }] });
  const updateIng = (idx, patch) => {
    const next = [...form.ingredients];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, ingredients: next });
  };
  const removeIng = (idx) => setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) });

  const ingTotal = form.ingredients.reduce((s, i) => s + (parseFloat(i.percentage) || 0), 0);

  return (
    <div data-testid="blends-page">
      <PageHeader
        eyebrow="Operations"
        title="Recipes / Blends"
        description="Define each saleable SKU as a percentage mix of origins. Drives per-blend cost calculations."
        actions={canWrite && (
          <button onClick={openNew} data-testid="blends-add-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <Plus className="w-4 h-4" /> New blend
          </button>
        )}
      />

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="blends-table">
          <thead>
            <tr className="bg-secondary">
              <th className="label-tiny px-4 py-3 text-left">Blend</th>
              <th className="label-tiny px-4 py-3 text-left">SKU</th>
              <th className="label-tiny px-4 py-3 text-left">Composition</th>
              <th className="label-tiny px-4 py-3 text-left">Status</th>
              {canWrite && <th className="w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && blends.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                No blends yet. Define your first to enable per-blend costing.
              </td></tr>
            )}
            {blends.map((b) => (
              <tr key={b.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-2.5 font-semibold">{b.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{b.sku || "—"}</td>
                <td className="px-4 py-2.5 text-xs">
                  {(b.ingredients || []).map((i) => {
                    const o = origins.find((x) => x.id === i.origin_id);
                    return `${o?.name || "?"} ${i.percentage}%`;
                  }).join(" + ") || "—"}
                </td>
                <td className="px-4 py-2.5">
                  {b.is_active === false
                    ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-bold">inactive</span>
                    : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,36%)] font-bold">active</span>}
                </td>
                {canWrite && (
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => openEdit(b)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(b.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="blend-modal">
          <div className="bg-card border border-border rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
              <h3 className="font-display text-lg font-bold">{editId ? "Edit blend" : "New blend"}</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="blend-name"
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
                <Field label="SKU">
                  <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
              </div>
              <Field label="Description">
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="label-tiny">Ingredients ({ingTotal.toFixed(1)}%)</p>
                  <button type="button" onClick={addIng} data-testid="blend-add-ingredient"
                    className="text-xs px-2 py-1 border border-border rounded hover:bg-secondary inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add origin
                  </button>
                </div>
                <div className="space-y-2">
                  {form.ingredients.length === 0 && (
                    <p className="text-xs text-muted-foreground">Add at least one origin (optional — single-origin blends can have one at 100%).</p>
                  )}
                  {form.ingredients.map((ing, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                      <select value={ing.origin_id} onChange={(e) => updateIng(idx, { origin_id: e.target.value })}
                        className="px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary">
                        <option value="">Select origin</option>
                        {origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                      <input type="number" step="any" value={ing.percentage} onChange={(e) => updateIng(idx, { percentage: e.target.value })} placeholder="%"
                        className="w-24 px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                      <button type="button" onClick={() => removeIng(idx)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {ingTotal !== 0 && Math.abs(ingTotal - 100) > 0.5 && (
                  <p className="text-xs text-[hsl(31,65%,53%)] mt-1">Should sum to 100% — currently {ingTotal.toFixed(1)}%</p>
                )}
              </div>

              {error && <p className="text-destructive text-sm">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
                <button type="submit" data-testid="blend-submit" className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">{editId ? "Save changes" : "Create blend"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
